<template>
  <div class="relative" @click="handleAction" @keydown.enter="handleAction">
    <FormLabel :for="name || id" :label="label" :label-hint="labelHint" :required="required" :class="labelClasses"/>
    <div class="relative" :class="inputContainerClasses">
      <slot name="near-input"/>
      <input
        ref="inputEl"
        :id="id"
        :autocomplete="autocomplete"
        :autocorrect="autocorrect ? 'on' : 'off'"
        :autofill="autofill"
        :class="inputClassesComputed"
        :placeholder="placeholder"
        :readonly="readonly"
        :required="required"
        :type="currentType"
        :value="formatted"
        :name="name"
        :inputmode="inputMode"
        :style="inputStyles"
        :tabindex="tabIndex"
        class="w-full appearance-none leading-tight py-2.5 focus:outline-none"
        @focus="inputFocus"
        @blur="inputBlur"
        @change="emitEvent('change', $event)"
        @input="emitEvent('update:modelValue', $event)"
        @paste="enforceFormat"
        @keydown="enforceFormat"
        @keydown.enter.prevent
      />
      <template v-if="type === 'password'">
        <EyeIcon v-if="showPassword" @click="showPassword=false" @keydown.enter="showPassword=false"
                 @keydown.space="showPassword=false" class="input-icon" aria-label="Hide Password"/>
        <EyeSlashIcon v-else @click="showPassword=true" @keydown.enter="showPassword=true"
                      @keydown.space="showPassword=true" class="input-icon" aria-label="Show Password"/>
      </template>
      <template v-else-if="copy">
        <ClipboardIcon class="cursor-copy input-icon" aria-label="Copy to Clipboard"/>
      </template>
      <div v-else-if="type === 'search'" class="input-icon-container glint hover:brightness-95 active:brightness-90"
           :class="roundedFull ? 'rounded-full' : 'rounded-md'">
        <MagnifyingGlassIcon class="h-5 w-5 text-white"/>
      </div>
    </div>
    <p v-if="error" class="absolute -bottom-5 left-0 text-xs text-red-600">
      {{ error }}
    </p>
  </div>
</template>

<script setup>
import {ClipboardIcon, EyeIcon, EyeSlashIcon, MagnifyingGlassIcon} from "@heroicons/vue/24/outline";
import FormLabel from "./FormLabel.vue";
import {useInput} from "../composables/InputComponent";

const emit = defineEmits(["update:modelValue", "change", "input-blur", "input-focus"]);
const props = defineProps({
  label: String,
  labelHint: String,
  placeholder: String,
  modelValue: [String, Number],
  name: String,
  type: String,
  required: Boolean,
  error: String,
  format: String,
  maxLength: Number,
  autocomplete: String,
  autofocus: Boolean,
  roundedFull: Boolean,
  readonly: Boolean,
  inputClasses: String,
  inputContainerClasses: String,
  labelClasses: String,
  inputMode: String,
  notRoundedLeft: Boolean,
  copy: Boolean,
  prefix: String,
  prefixes: Array,
  autocorrect: Boolean,
  autofill: String,
  dark: Boolean,
  inputStyles: String,
  tabIndex: {
    type: String,
    default: "0",
  }
});

const {
  id,
  inputEl,
  showPassword,
  formatted,
  currentType,
  inputClassesComputed,
  inputMode,
  handleAction,
  enforceFormat,
  inputBlur,
  inputFocus,
  emitEvent,
} = useInput(props, emit);

defineExpose({inputEl});
</script>

<style scoped>
.input-icon {
  position: absolute;
  top: 50%;
  right: 1rem;
  transform: translateY(-50%);
  height: 1.25rem;
  width: 1.25rem;
  color: gray;
  cursor: pointer;
}

.input-icon-container {
  position: absolute;
  top: 0.175rem;
  right: 0.175rem;
  bottom: 0.175rem;
  display: flex;
  aspect-ratio: 1;
  cursor: pointer;
  align-items: center;
  justify-content: center;
  background: var(--color-gradient);
}
</style>
